import type { FeatureFlagKey, FeatureFlagSnapshot } from "./feature-flags";

/**
 * Structured local event log. R1 Step 15.
 *
 * Doc 02 section 4: every catch block either recovers visibly or surfaces, and
 * a swallowed exception is a defect. This is where a surfaced one goes.
 *
 * Entirely local. Nothing here is ever sent anywhere; the export is written to
 * the device as a file the trainee sends however he likes.
 */
export interface LogEvent {
  readonly action: string;
  readonly message: string;
  readonly module: string;
  readonly schemaVersion: 1;
  readonly stack: string | null;
  readonly at: string;
}

/** Doc 02 section 4: the export carries the last fifty entries. */
export const EVENT_LOG_CAPACITY = 50;

export interface EventLog {
  append(entry: Omit<LogEvent, "at" | "schemaVersion">): void;
  clear(): void;
  entries(): readonly LogEvent[];
}

/**
 * A bounded ring. The oldest entry is dropped once capacity is reached, so a
 * long session cannot grow without limit on a phone.
 */
export function createEventLog(
  now: () => string,
  capacity: number = EVENT_LOG_CAPACITY,
): EventLog {
  if (!Number.isInteger(capacity) || capacity < 1) {
    throw new RangeError("event log capacity must be a positive integer");
  }
  let held: LogEvent[] = [];

  return {
    append: (entry) => {
      held.push({ ...entry, schemaVersion: 1, at: now() });
      if (held.length > capacity) held = held.slice(held.length - capacity);
    },
    clear: () => {
      held = [];
    },
    entries: () => [...held],
  };
}

/** Turns an unknown thrown value into a loggable entry without losing it. */
export function describeFailure(
  module: string,
  action: string,
  failure: unknown,
): Omit<LogEvent, "at" | "schemaVersion"> {
  if (failure instanceof Error) {
    return {
      module,
      action,
      message: failure.message,
      stack: failure.stack ?? null,
    };
  }
  return {
    module,
    action,
    message: typeof failure === "string" ? failure : JSON.stringify(failure),
    stack: null,
  };
}

export interface BugReportState {
  readonly activeFileHash: string | null;
  readonly appVersion: string;
  readonly featureFlags: FeatureFlagSnapshot;
  readonly storedRecordKeys: readonly string[];
}

export interface BugReport {
  readonly schemaVersion: 1;
  readonly capturedAt: string;
  readonly events: readonly LogEvent[];
  readonly state: BugReportState;
}

/**
 * Builds the one-tap bug report: the last fifty entries, the current versioned
 * state, and the active file hash.
 *
 * **The file hash only.** Never the file, never its text. The hash identifies
 * which document was loaded without carrying any of it, which is what hard
 * rule 2 requires.
 */
export function buildBugReport(
  log: EventLog,
  state: BugReportState,
  capturedAt: string,
): BugReport {
  return {
    schemaVersion: 1,
    capturedAt,
    events: log.entries(),
    state,
  };
}

/** File name for a saved report. Carries no user content. */
export function bugReportFileName(capturedAt: string): string {
  return `atlas-bug-report-${capturedAt.replaceAll(/[^0-9A-Za-z]/g, "-")}.json`;
}

/**
 * Storage keys are namespaced and hashed, so listing them is safe. Any key
 * that does not match the application's own namespace is excluded rather than
 * reported, because it did not come from ATLas.
 */
export function atlasStorageKeys(
  allKeys: readonly string[],
): readonly string[] {
  return allKeys.filter((key) => key.startsWith("r1:")).sort();
}

export function enabledFlagKeys(
  snapshot: FeatureFlagSnapshot,
): readonly FeatureFlagKey[] {
  return (Object.keys(snapshot) as FeatureFlagKey[]).filter(
    (key) => snapshot[key],
  );
}
